/**
 * What "quiet" is, on this microphone, in this room, right now.
 *
 * Every level threshold in Channel B is really a statement about the difference
 * between a sound and the background it sits in, and only one of those two is
 * knowable in advance. A fixed floor picks a number for the background and is
 * therefore wrong everywhere it was not fitted.
 *
 * How wrong: `FEATURE_FLOOR_DB` was -70, chosen against synthetic signals. On a
 * real take it gated **5 frames out of 1672**, because that room and microphone
 * sat around -40 dB. Everything the floor existed to suppress went straight
 * through it — most visibly the attack detector, which measured the background's
 * own roughness at a median of 6.8 dB with peaks of 13.7, against a plosive
 * threshold of 15. Two decibels of headroom between "somebody clapped" and
 * "the room exists".
 *
 * ### Why a percentile of block minima
 *
 * The obvious estimator is an exponential minimum follower. It latches: one
 * dropped buffer of digital silence pins the floor at -140 dB and it takes the
 * whole rise constant to climb back. Fitting one against a recording put its
 * 5th percentile at -105 dB, which is not a room, it is a gap.
 *
 * So instead: take the minimum within each short block, keep a few seconds of
 * those, and report their median. A brief dropout moves one block out of forty.
 * A loud passage has to fill half the window before it moves the floor at all.
 * And because it is a rank statistic rather than an average, a clap does not
 * drag it up in proportion to how loud the clap was.
 *
 * The gate — the level a sound has to clear to be worth describing — is the
 * floor plus a margin, and that margin is the one number here that is still a
 * guess. It could not be fitted from the recording that motivated all of this,
 * because the speaker was playing music throughout to make the take harder:
 * there was no quiet in it to measure. See NOISE.MARGIN_DB in config.js.
 */

import { config } from '../core/config.js';

export class NoiseFloor {
  /**
   * @param {object} [o]
   * @param {number} [o.rate]     envelope samples per second
   * @param {number} [o.seconds]  history length
   * @param {number} [o.block]    samples per block minimum
   */
  constructor({
    rate = 375,
    seconds = config.audio.NOISE.SECONDS,
    block = config.audio.NOISE.BLOCK,
  } = {}) {
    this.seconds = seconds;
    this.block = Math.max(1, Math.floor(block));
    this.setRate(rate);
  }

  setRate(rate) {
    this.rate = rate;
    /** Block minima, oldest first. @type {number[]} */
    this._history = [];
    this._capacity = Math.max(3, Math.round((this.seconds * rate) / this.block));
    this._min = Number.POSITIVE_INFINITY;
    this._n = 0;
    /**
     * Until enough blocks have been seen the estimate is not trustworthy, so
     * `floor` reports the configured fallback rather than a confident number
     * derived from a quarter of a second.
     */
    this._floor = null;
  }

  /** True once the history is deep enough to mean something. */
  get ready() {
    return this._floor !== null;
  }

  /**
   * The background level in dB. Before enough history exists this is the
   * configured fallback — deliberately low, so nothing is gated out on the
   * strength of a guess.
   */
  get floor() {
    return this._floor ?? config.audio.NOISE.FALLBACK_DB;
  }

  /** The level a sound has to clear to be described. */
  get gate() {
    return this.floor + config.audio.NOISE.MARGIN_DB;
  }

  /**
   * Fold in one envelope sample's level.
   * @param {number} db
   */
  push(db) {
    if (!Number.isFinite(db)) return this.floor;
    if (db < this._min) this._min = db;
    this._n++;
    if (this._n < this.block) return this.floor;

    this._history.push(this._min);
    if (this._history.length > this._capacity) this._history.shift();
    this._min = Number.POSITIVE_INFINITY;
    this._n = 0;

    // A quarter of the window is enough to stop reporting the fallback; the
    // estimate keeps improving from there.
    if (this._history.length >= Math.max(3, this._capacity >> 2)) {
      const sorted = [...this._history].sort((a, b) => a - b);
      this._floor = sorted[sorted.length >> 1];
    }
    return this.floor;
  }

  reset() {
    this.setRate(this.rate);
  }
}

export default NoiseFloor;
